import {
  portfolioPerformance,
  publicPortfolioPerformance,
  CreateCashFlowAndPerformanceStatisticsParams,
  PortfolioPerformanceResult,
  PortfolioPerformanceResultList,
} from "@rentablo/api/apiv1";
import { useAuthQuery, type UseQueryExtendedOptions } from "@rentablo/api";
import { useSharedPortfolioContext } from "@providers/SharedPortfolioProvider";
import { keepPreviousData } from "@tanstack/react-query";

export const usePublicOrPrivatePerformance = (
  params?: CreateCashFlowAndPerformanceStatisticsParams,
  options?: UseQueryExtendedOptions<
    PortfolioPerformanceResultList,
    AxiosApiError,
    PortfolioPerformanceResultList
  >
) => {
  const { publicId, isSharedPortfolio } = useSharedPortfolioContext();
  const hasAccountIds = Boolean(params?.accountIds?.length);
  const enabledOption = options?.enabled ?? true;
  const enabled = hasAccountIds && enabledOption && Boolean(params);

  const query = useAuthQuery<
    PortfolioPerformanceResultList,
    AxiosApiError,
    PortfolioPerformanceResultList
  >(
    [
      "public-or-private-performance",
      isSharedPortfolio ? "public" : "private",
      publicId ?? null,
      params,
    ],
    ({ token }) => {
      if (isSharedPortfolio) {
        return publicPortfolioPerformance(token, {
          publicId: publicId!,
          createCashFlowAndPerformanceStatisticsParams: params!,
        });
      }

      return portfolioPerformance(token, {
        createCashFlowAndPerformanceStatisticsParams: params!,
      });
    },
    {
      ...options,
      enabled,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      placeholderData: keepPreviousData,
    }
  );

  const performanceData: PortfolioPerformanceResult[] =
    query.data?.portfolioPerformanceResults ?? [];

  return {
    data: performanceData,
    currency: query.data?.displayCurrency,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isFetched: query.isFetched,
    error: query.error,
  };
};
